import { useCallback, useEffect, useRef, useState } from "react";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { useUI } from "@/context/UIContext";
import { MapSearch } from "@/components/common/MapSearch";
import { cn } from "@/lib/utils";
import { Plus, Minus, Layers, Locate, Maximize2, Minimize2, MapPinOff } from "lucide-react";
import { carIcon } from "./vehicleMarker";

// Real Google Maps-backed fleet map. Same prop contract the old illustrative
// SVG placeholder used (vehicles/geofences/facilities/route/selectedId/
// onSelect/...), so every page that renders a map needed no changes beyond
// feeding it real lat/lng data instead of mock x/y grid coordinates.

// Brighter than the raw Tailwind 400/500 shades so a vehicle stands out
// against both the dark and light road styles at a glance, not just on hover.
const STATUS_COLOR = {
  moving: "#22ee9c",
  alert: "#ff2d55",
  idle: "#ffd60a",
  offline: "#9aa1ad",
  parked: "#22c3ff",
};

const DEFAULT_CENTER = { lat: 32.7767, lng: -96.797 }; // Dallas, TX — falls back here only if nothing on the map has a real position yet
const DEFAULT_ZOOM = 11;

// Clicking a vehicle's arrow frames that vehicle. Deliberately ~70% of the
// way up Google's zoom scale rather than the maximum: at 20-21 the imagery
// goes soft and every bit of surrounding context (which yard, which road, what
// else is nearby) is off-screen, which is the wrong trade for a fleet map.
const MAX_ZOOM = 21; // Google's ceiling for the base map types
const FOCUS_ZOOM = Math.round(MAX_ZOOM * 0.7); // 15

// Pinpointing a single building (a facility gate) wants to be closer in than
// "find this vehicle" does.
const PIN_ZOOM = 17;

// Last center/zoom per `viewportKey`. Module-level on purpose: it survives
// the component unmounting (a route change, or a parent briefly swapping in a
// loading skeleton) so the user comes back to where they were looking instead
// of the map snapping back to its default framing. In memory rather than
// storage, so it resets on a real page reload — the intended scope.
//
// Opt-in per caller rather than keyed off testId: maps that frame a specific
// entity (one trip's route, one facility, one vehicle) SHOULD re-fit to that
// entity's own data, and sharing a key across them would restore the previous
// trip's viewport onto the next one. Only fleet-wide maps pass a key.
const VIEWPORT_MEMORY = new Map();

// Google's standard "night mode" recipe, tuned slightly to sit closer to
// the app's near-black/blue palette instead of the stock navy.
//
// Place names are deliberately left ON throughout. An earlier version turned
// `poi` and `transit` off wholesale and killed road label icons, which made
// the map read as an anonymous grid — you could see a vehicle was somewhere
// but not *where*. Only geometry fills are darkened here; every label layer
// keeps Google's own zoom-dependent behaviour, so names thin out as you zoom
// out and fill in as you zoom in, exactly like maps.google.com.
const DARK_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0f1115" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0f1115" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9aa1ad" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#3a3f4b" }] },
  // The one label layer still hidden: parcel numbers are pure noise on a
  // fleet map and crowd out the street and business names that matter.
  { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d4d8e0" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#191c23" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#949aa6" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#12251c" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6aa383" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1b1e26" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#a4abb7" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#22252f" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2a2e3a" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#c8cdd6" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#1b1e26" }] },
  { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#949aa6" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0b1220" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4a5568" }] },
];

// The app's light theme's counterpart to DARK_STYLE above — same feature/
// element coverage (place names on, parcel labels off), just repainted for
// a white-ish base instead of near-black, so the map follows the light/dark
// toggle in UIContext the same way every other surface in the app does.
// Satellite/hybrid imagery is untouched by either style (see SATELLITE_MAP_TYPE
// below, always styles: []) — there's no "light satellite" to switch to.
const LIGHT_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#f5f6f8" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#5b6270" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#c7cad1" }] },
  { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#3d4250" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#e9eaee" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#7a8091" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#dcefe0" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#5a9c72" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#f0f1f4" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#e6e8ed" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#454b57" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#f0f1f4" }] },
  { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#7a8091" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9e0f2" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4a7a9c" }] },
];

// "satellite" as a variant means imagery WITH the label/road overlay —
// Google's `hybrid` type. Plain `satellite` draws no labels at all, which is
// why the imagery view used to be nameless.
const SATELLITE_MAP_TYPE = "hybrid";

// Every vehicle gets the car sprite regardless of status, oriented to its
// heading — a parked or offline vehicle still has a last-known facing, and a
// plain dot is strictly less information for the same space on the map. Only
// the body colour changes by status. See vehicleMarker.js.
// Speed-band coloring for trip routes (mph — same bands and colors
// Bouncie's own trip playback uses, so a route looks the same whether
// viewed there or here).
export const SPEED_BANDS = [
  { max: 35, color: "#f97316", label: "0-35 MPH" },
  { max: 45, color: "#22c55e", label: "35-45 MPH" },
  { max: 50, color: "#3b82f6", label: "45-50 MPH" },
  { max: 65, color: "#d946ef", label: "50-65 MPH" },
  { max: Infinity, color: "#ef4444", label: "65+ MPH" },
];

// Exported so the trip timeline's speed profile uses the same bands as the
// route drawn on the map — two different colourings of the same telemetry on
// one screen would read as two different measurements.
export function speedBandColor(mph) {
  return (SPEED_BANDS.find((b) => mph < b.max) || SPEED_BANDS[SPEED_BANDS.length - 1]).color;
}

// Style for a shape being drawn (geofence creation). Editable + draggable so
// the user can fine-tune the shape after drawing it, before saving.
const DRAW_STYLE = {
  fillColor: "#f43f5e", fillOpacity: 0.2, strokeColor: "#f43f5e", strokeOpacity: 0.95, strokeWeight: 2,
  editable: true, draggable: true, zIndex: 50,
};

// Haversine, in meters, over two google.maps.LatLng values. Hand-rolled
// rather than google.maps.geometry.spherical so drawing depends on nothing
// beyond the core API (see the drawing note in MapCanvas).
function metersBetween(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const lat1 = a.lat(), lng1 = a.lng(), lat2 = b.lat(), lng2 = b.lng();
  const p1 = toRad(lat1), p2 = toRad(lat2);
  const dp = toRad(lat2 - lat1), dl = toRad(lng2 - lng1);
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function vehicleIcon(maps, v, selected) {
  // Was a FORWARD_CLOSED_ARROW symbol. The car sprite carries the same two
  // pieces of information — status by colour, heading by rotation — and reads
  // as a vehicle rather than a cursor. See vehicleMarker.js for why the
  // rotation is baked into the SVG instead of using icon.rotation.
  return carIcon(maps, {
    color: STATUS_COLOR[v.status] || "#22c3ff",
    heading: v.heading || 0,
    size: selected ? 56 : 44,
  });
}

export function MapCanvas({
  vehicles = [], geofences = [], facilities = [], route = null,
  // "dark" = the styled road map, which is the default: it is the view that
  // answers where a vehicle is on the network, and place/road labels stay
  // legible at every zoom. Imagery is one click away on the layer toggle for
  // when the question is what is physically at a location — a yard, a gate.
  selectedId, onSelect, variant = "dark", className, controls = true,
  showBoundaries = true, testId, viewportKey, search = true,
  // Anything the page wants layered over the map. It renders INSIDE this
  // component's root — which is also the element that goes fullscreen — so
  // page-level map controls survive fullscreen instead of being left behind
  // outside the only subtree the browser still paints. onFullscreenChange
  // hands back that element so a page can portal dialogs into it too.
  overlay, onFullscreenChange,
  // Drawing (used by the Geofences page to draw a new geofence directly on
  // this map). `drawingActive` turns the tool on at all; `drawingMode` picks
  // the shape. onShapeDrawn fires on completion AND on every later edit of
  // that shape, so the caller's copy of the geometry is always current.
  // onDrawProgress reports in-progress polygon vertex count; bumping
  // finishSignal closes the polygon (the page's "Finish" button).
  // drawingMode: "circle" | "polygon" | "pin". `initialPin` seeds and frames
  // the pin so the map opens on the address the user already chose instead of
  // making them hunt for it.
  drawingActive = false, drawingMode = null, onShapeDrawn, onDrawProgress, finishSignal = 0,
  initialPin = null,
}) {
  // Read as primitives so a caller passing a fresh `{lat,lng}` object each
  // render cannot re-arm the tool and throw away a pin the user just dragged.
  const initialPinLat = initialPin?.lat ?? null;
  const initialPinLng = initialPin?.lng ?? null;
  const { maps, error } = useGoogleMaps();
  const { theme } = useUI();
  const roadStyle = theme === "light" ? LIGHT_STYLE : DARK_STYLE;
  const containerRef = useRef(null);
  const rootRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const boundsFitRef = useRef(false);
  const [layer, setLayer] = useState(variant);
  // The map object also lives in state, not just mapRef, because the search
  // box is a React child that needs to re-render once the map exists.
  const [mapInstance, setMapInstance] = useState(null);
  const isSat = layer === "satellite";
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Drawing state lives in refs, deliberately separate from overlaysRef —
  // that array is cleared and rebuilt on every data change, which would
  // otherwise wipe a shape mid-draw the moment any poll refreshed.
  //
  // Drawing is implemented with the CORE Maps API (Circle/Polygon/Marker +
  // map mouse events) rather than google.maps.drawing.DrawingManager. The
  // drawing library ships as a separate async script that the loader fetches
  // alongside main.js, and the `callback` fires when main.js is ready — so
  // `google.maps.drawing` is not reliably present when we need it, and this
  // key's bootstrap exposes no importLibrary() to await it either. Circle,
  // Polygon and Marker are core and already proven on this map (existing
  // geofences render with them), so building on those removes an entire
  // class of load-order failure rather than trying to time around it.
  const drawnOverlayRef = useRef(null);
  const drawnListenersRef = useRef([]);
  const toolListenersRef = useRef([]);
  const polyPointsRef = useRef([]);
  // Every overlay belonging to an unfinished shape (circle preview, polygon
  // vertex dots and guide line). Held in a ref so switching tool, cancelling,
  // or unmounting can remove them all — a local closure variable would leak
  // the preview onto the map.
  const progressOverlaysRef = useRef([]);
  // Which initial pin the viewport has already been framed on, so re-arming
  // the tool doesn't yank the map back and undo the user's own panning.
  const pinFramedRef = useRef(null);
  // Callbacks via refs so listeners registered once never call a stale
  // closure from an earlier render.
  const onShapeDrawnRef = useRef(onShapeDrawn);
  const onDrawProgressRef = useRef(onDrawProgress);
  const onFullscreenChangeRef = useRef(onFullscreenChange);
  useEffect(() => { onShapeDrawnRef.current = onShapeDrawn; }, [onShapeDrawn]);
  useEffect(() => { onDrawProgressRef.current = onDrawProgress; }, [onDrawProgress]);
  useEffect(() => { onFullscreenChangeRef.current = onFullscreenChange; }, [onFullscreenChange]);

  const clearDrawnShape = useCallback(() => {
    drawnListenersRef.current.forEach((l) => l.remove?.());
    drawnListenersRef.current = [];
    drawnOverlayRef.current?.setMap(null);
    drawnOverlayRef.current = null;
  }, []);

  const clearProgress = useCallback(() => {
    progressOverlaysRef.current.forEach((o) => o.setMap(null));
    progressOverlaysRef.current = [];
    polyPointsRef.current = [];
    onDrawProgressRef.current?.({ pointCount: 0, phase: null });
  }, []);

  // Init the map exactly once per mount.
  useEffect(() => {
    if (!maps || !containerRef.current || mapRef.current) return;
    const remembered = viewportKey ? VIEWPORT_MEMORY.get(viewportKey) : null;
    const map = new maps.Map(containerRef.current, {
      center: remembered?.center || DEFAULT_CENTER,
      zoom: remembered?.zoom ?? DEFAULT_ZOOM,
      disableDefaultUI: true,
      // Place names and POI icons are drawn, but not clickable: tapping one
      // would open Google's own info window over the fleet, and on the
      // Geofences page it would eat the click that places a corner.
      clickableIcons: false,
      mapTypeId: variant === "satellite" ? SATELLITE_MAP_TYPE : "roadmap",
      styles: variant === "satellite" ? [] : roadStyle,
    });
    // A restored viewport is the user's own framing, so suppress the
    // fit-to-data pass below that would otherwise immediately override it.
    if (remembered) boundsFitRef.current = true;

    map.addListener("click", () => onSelect?.(null));
    // "idle" fires once a pan/zoom has settled — one hook covers dragging,
    // scroll-zoom and the +/- buttons alike.
    if (viewportKey) {
      map.addListener("idle", () => {
        const c = map.getCenter();
        if (!c) return;
        VIEWPORT_MEMORY.set(viewportKey, {
          center: { lat: c.lat(), lng: c.lng() },
          zoom: map.getZoom(),
        });
      });
    }
    mapRef.current = map;
    setMapInstance(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maps]);

  // Map/Satellite toggle, and re-applied whenever the app's light/dark theme
  // changes so an already-mounted map restyles live instead of only on next
  // mount. Satellite/hybrid stays untouched either way (styles: []).
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setMapTypeId(isSat ? SATELLITE_MAP_TYPE : "roadmap");
    mapRef.current.setOptions({ styles: isSat ? [] : roadStyle });
  }, [isSat, roadStyle]);

  // Mirror the browser's own fullscreen state instead of flipping a flag in
  // the button's own handler: Esc and the browser's native controls also exit
  // fullscreen, and a self-managed flag would be left inverted after that,
  // leaving the button doing the opposite of what its icon says.
  useEffect(() => {
    const sync = () => {
      const active = document.fullscreenElement || document.webkitFullscreenElement || null;
      const mine = !!active && active === rootRef.current;
      setIsFullscreen(mine);
      onFullscreenChangeRef.current?.({ isFullscreen: mine, element: mine ? rootRef.current : null });
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    sync();
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggleFullscreen = () => {
    const target = rootRef.current;
    if (!target) return;
    // This used to unconditionally call requestFullscreen(), which is a no-op
    // once already fullscreen — so there was no way back out via the button.
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      exit?.call(document)?.catch?.(() => {});
    } else {
      const request = target.requestFullscreen || target.webkitRequestFullscreen;
      request?.call(target)?.catch?.(() => {});
    }
  };

  // Center the map on a clicked vehicle. Only ever zooms IN — someone already
  // looking closer than FOCUS_ZOOM at a neighbouring vehicle should be panned
  // across, not yanked back out.
  const focusOn = useCallback((position) => {
    const map = mapRef.current;
    if (!map) return;
    map.panTo(position);
    if ((map.getZoom() ?? 0) < FOCUS_ZOOM) map.setZoom(FOCUS_ZOOM);
  }, []);

  // Emit the live geometry of a finished shape, and keep emitting as the
  // user nudges it, so the caller never saves stale coordinates.
  const emitShape = useCallback((overlay) => {
    // Marker (pin mode) — a Marker has getPosition and neither getRadius nor
    // a path, so this check has to come first.
    if (typeof overlay.getPosition === "function") {
      const p = overlay.getPosition();
      onShapeDrawnRef.current?.({ type: "point", lat: p.lat(), lng: p.lng() });
      return;
    }
    if (typeof overlay.getRadius === "function") {
      const c = overlay.getCenter();
      onShapeDrawnRef.current?.({
        type: "circle",
        centerLat: c.lat(), centerLng: c.lng(),
        radiusM: Math.max(Math.round(overlay.getRadius()), 1),
      });
    } else {
      onShapeDrawnRef.current?.({
        type: "polygon",
        coordinates: overlay.getPath().getArray().map((p) => [p.lat(), p.lng()]),
      });
    }
  }, []);

  const finalizeShape = useCallback((overlay) => {
    clearDrawnShape(); // one shape at a time
    drawnOverlayRef.current = overlay;
    overlay.setOptions({ editable: true, draggable: true });

    const ev = maps.event;
    if (typeof overlay.getRadius === "function") {
      drawnListenersRef.current.push(
        ev.addListener(overlay, "center_changed", () => emitShape(overlay)),
        ev.addListener(overlay, "radius_changed", () => emitShape(overlay))
      );
    } else {
      const path = overlay.getPath();
      drawnListenersRef.current.push(
        ev.addListener(path, "set_at", () => emitShape(overlay)),
        ev.addListener(path, "insert_at", () => emitShape(overlay)),
        ev.addListener(path, "remove_at", () => emitShape(overlay)),
        ev.addListener(overlay, "dragend", () => emitShape(overlay))
      );
    }
    emitShape(overlay);
  }, [maps, clearDrawnShape, emitShape]);

  const finishPolygon = useCallback(() => {
    const pts = polyPointsRef.current;
    if (!maps || !mapRef.current || pts.length < 3) return;
    const polygon = new maps.Polygon({ ...DRAW_STYLE, paths: [...pts], map: mapRef.current });
    clearProgress();
    finalizeShape(polygon);
  }, [maps, clearProgress, finalizeShape]);

  // Arm the active tool: plain map mouse listeners, no drawing library.
  useEffect(() => {
    if (!maps || !mapRef.current) return;
    const map = mapRef.current;
    const ev = maps.event;

    // Always tear down the previous tool before arming the next one.
    toolListenersRef.current.forEach((l) => l.remove?.());
    toolListenersRef.current = [];
    clearProgress();

    if (!drawingActive || !drawingMode) {
      map.setOptions({ draggable: true, draggableCursor: null });
      return;
    }

    // Starting a new shape discards the previous one — picking a tool again
    // means "start over", and leaving the old overlay up would show two
    // shapes when only one is real.
    clearDrawnShape();
    map.setOptions({ draggableCursor: "crosshair" });

    if (drawingMode === "circle") {
      // Click to drop the center, move to size it, click again to finish.
      //
      // This replaced press-drag-release, which could not be finished at all:
      // the preview circle grows out from under the cursor, and a Circle is
      // clickable by default, so it captured the pointer and the map's own
      // mouseup never fired — the circle just kept following the mouse with
      // no way to commit it. Two clicks need no mouseup, and the preview is
      // explicitly clickable:false so it can never intercept either click.
      let center = null;
      let preview = null;

      toolListenersRef.current.push(
        ev.addListener(map, "click", (e) => {
          if (!center) {
            center = e.latLng;
            preview = new maps.Circle({
              ...DRAW_STYLE,
              editable: false, draggable: false, clickable: false,
              center, radius: 1, map,
            });
            progressOverlaysRef.current.push(preview);
            onDrawProgressRef.current?.({ pointCount: 1, phase: "sizing" });
            return;
          }
          // Second click commits it. Floored at 10m so a stray double-click
          // can't create a zero-radius geofence nothing could ever enter.
          const radius = Math.max(metersBetween(center, e.latLng), 10);
          const committedCenter = center;
          center = null;
          preview = null;
          clearProgress();
          finalizeShape(new maps.Circle({ ...DRAW_STYLE, center: committedCenter, radius, map }));
        }),
        ev.addListener(map, "mousemove", (e) => {
          if (!center || !preview) return;
          preview.setRadius(Math.max(metersBetween(center, e.latLng), 1));
        })
      );
    } else if (drawingMode === "polygon") {
      // Click per corner; double-click (or the caller's Finish action)
      // closes the ring.
      map.setOptions({ draggable: true });

      const redrawPreview = () => {
        // Rebuild the guide line from scratch — simpler than mutating a
        // MVCArray and cheap at the handful of points a geofence has.
        progressOverlaysRef.current.forEach((o) => o.setMap(null));
        progressOverlaysRef.current = [];
        const pts = polyPointsRef.current;
        if (pts.length > 1) {
          progressOverlaysRef.current.push(
            new maps.Polyline({
              path: [...pts], map,
              // clickable:false for the same reason as the circle preview —
              // the guide line runs between the corners just placed, so a
              // clickable one would swallow the click for the next corner.
              clickable: false,
              strokeColor: DRAW_STYLE.strokeColor, strokeOpacity: 0.95, strokeWeight: 2,
            })
          );
        }
        pts.forEach((p) => {
          progressOverlaysRef.current.push(
            new maps.Marker({
              position: p, map, clickable: false,
              icon: {
                path: maps.SymbolPath.CIRCLE, scale: 4,
                fillColor: DRAW_STYLE.strokeColor, fillOpacity: 1,
                strokeColor: "#ffffff", strokeWeight: 1.5,
              },
            })
          );
        });
        onDrawProgressRef.current?.({ pointCount: pts.length, phase: "corners" });
      };

      toolListenersRef.current.push(
        ev.addListener(map, "click", (e) => {
          polyPointsRef.current = [...polyPointsRef.current, e.latLng];
          redrawPreview();
        }),
        ev.addListener(map, "dblclick", () => finishPolygon())
      );
    } else if (drawingMode === "pin") {
      // One draggable marker: click anywhere to move it, drag to fine-tune.
      // No "finish" step — a point is complete the moment it exists, which is
      // what makes Save immediately available.
      const place = (position) => {
        clearDrawnShape();
        const marker = new maps.Marker({
          position, map, draggable: true, zIndex: 60,
          icon: {
            path: maps.SymbolPath.CIRCLE, scale: 8,
            fillColor: DRAW_STYLE.strokeColor, fillOpacity: 1,
            strokeColor: "#ffffff", strokeWeight: 2,
          },
        });
        drawnOverlayRef.current = marker;
        drawnListenersRef.current.push(
          ev.addListener(marker, "dragend", () => emitShape(marker))
        );
        emitShape(marker);
      };

      if (initialPinLat != null && initialPinLng != null) {
        const start = new maps.LatLng(initialPinLat, initialPinLng);
        place(start);
        const key = `${initialPinLat},${initialPinLng}`;
        if (pinFramedRef.current !== key) {
          pinFramedRef.current = key;
          map.setCenter(start);
          if ((map.getZoom() ?? 0) < PIN_ZOOM) map.setZoom(PIN_ZOOM);
        }
      }

      toolListenersRef.current.push(
        ev.addListener(map, "click", (e) => place(e.latLng))
      );
    }

    return () => {
      toolListenersRef.current.forEach((l) => l.remove?.());
      toolListenersRef.current = [];
      map.setOptions({ draggable: true, draggableCursor: null });
    };
  }, [maps, drawingActive, drawingMode, initialPinLat, initialPinLng,
      clearDrawnShape, clearProgress, finalizeShape, finishPolygon, emitShape]);

  // The caller's explicit "Finish" action for polygons (a bare click-to-close
  // ring is fiddly to hit, so the page offers a button as well as dblclick).
  useEffect(() => {
    if (!finishSignal) return;
    finishPolygon();
  }, [finishSignal, finishPolygon]);

  // Leaving draw mode discards whatever was in progress.
  useEffect(() => {
    if (drawingActive) return;
    clearDrawnShape();
    clearProgress();
  }, [drawingActive, clearDrawnShape, clearProgress]);

  // Unmount cleanup — leaked listeners would keep firing against a dead map.
  useEffect(() => () => {
    drawnListenersRef.current.forEach((l) => l.remove?.());
    toolListenersRef.current.forEach((l) => l.remove?.());
    drawnOverlayRef.current?.setMap(null);
    progressOverlaysRef.current.forEach((o) => o.setMap(null));
  }, []);

  // Redraw every overlay whenever the data changes. Markers/shapes are
  // cheap enough at fleet-dashboard scale that a full clear-and-redraw is
  // simpler and safer than diffing, and avoids stale-marker bugs.
  useEffect(() => {
    if (!maps || !mapRef.current) return;
    const map = mapRef.current;
    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];
    const bounds = new maps.LatLngBounds();
    let hasBoundsPoint = false;
    const extend = (pos) => { bounds.extend(pos); hasBoundsPoint = true; };

    if (showBoundaries) {
      // Facilities only carry a single lat/lng, not a recorded boundary
      // shape, so a small fixed-radius circle stands in for "the yard".
      facilities.forEach((f) => {
        if (f.latitude == null || f.longitude == null) return;
        const pos = { lat: f.latitude, lng: f.longitude };
        overlaysRef.current.push(
          new maps.Circle({
            center: pos, radius: 180, map,
            // Opacity tuned for the satellite basemap (the app-wide
            // default) — the low values this used to have were legible
            // against the flat dark roadmap but nearly invisible against
            // photographic satellite imagery's visual noise.
            fillColor: "#38bdf8", fillOpacity: 0.16,
            strokeColor: "#38bdf8", strokeOpacity: 0.85, strokeWeight: 2,
          }),
          new maps.Marker({
            position: pos, map, clickable: false,
            icon: { path: maps.SymbolPath.CIRCLE, scale: 0 },
            label: { text: f.name, color: "#7dd3fc", fontSize: "11px", fontWeight: "600" },
          })
        );
        extend(pos);
      });

      geofences.forEach((g) => {
        if (g.status === "disabled") return;
        if (g.type === "circle" && g.centerLat != null && g.centerLng != null) {
          const pos = { lat: g.centerLat, lng: g.centerLng };
          overlaysRef.current.push(
            new maps.Circle({
              center: pos, radius: g.radius || 300, map,
              fillColor: "#f43f5e", fillOpacity: 0.18,
              strokeColor: "#f43f5e", strokeOpacity: 0.9, strokeWeight: 2,
            })
          );
          extend(pos);
        } else if (g.polygonCoordinates?.length) {
          const path = g.polygonCoordinates.map(([lat, lng]) => ({ lat, lng }));
          overlaysRef.current.push(
            new maps.Polygon({
              paths: path, map,
              fillColor: "#fbbf24", fillOpacity: 0.18,
              strokeColor: "#fbbf24", strokeOpacity: 0.9, strokeWeight: 2,
            })
          );
          path.forEach(extend);
        }
      });
    }

    if (route && route.length > 1) {
      const pts = route.filter((p) => p.lat != null && p.lng != null);
      if (pts.length > 1) {
        // Recorded GPS points carry a speed (mph — see MapCanvas's vehicle
        // icon comment on units); when they do, draw the route as one
        // short segment per point-pair colored by that segment's speed
        // band, matching Bouncie's own trip-playback view. Falls back to a
        // single solid line when speed wasn't recorded for these points.
        const hasSpeed = pts.some((p) => typeof p.speed === "number");
        if (hasSpeed) {
          for (let i = 0; i < pts.length - 1; i++) {
            const a = pts[i], b = pts[i + 1];
            const segSpeed = ((a.speed ?? b.speed ?? 0) + (b.speed ?? a.speed ?? 0)) / 2;
            overlaysRef.current.push(
              new maps.Polyline({
                path: [{ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }],
                map, strokeColor: speedBandColor(segSpeed), strokeOpacity: 0.9, strokeWeight: 4,
              })
            );
          }
        } else {
          overlaysRef.current.push(
            new maps.Polyline({ path: pts.map((p) => ({ lat: p.lat, lng: p.lng })), map, strokeColor: "#ef4444", strokeOpacity: 0.9, strokeWeight: 4 })
          );
        }
        overlaysRef.current.push(
          new maps.Marker({ position: { lat: pts[0].lat, lng: pts[0].lng }, map, icon: { path: maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#34d399", fillOpacity: 1, strokeColor: "#0a0a0b", strokeWeight: 2 } }),
          new maps.Marker({ position: { lat: pts[pts.length - 1].lat, lng: pts[pts.length - 1].lng }, map, icon: { path: maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#f43f5e", fillOpacity: 1, strokeColor: "#0a0a0b", strokeWeight: 2 } })
        );
        pts.forEach((p) => extend({ lat: p.lat, lng: p.lng }));
      }
    }

    vehicles.forEach((v) => {
      const lat = v.location?.lat, lng = v.location?.lng;
      if (lat == null || lng == null) return;
      const pos = { lat, lng };
      const selected = selectedId === v.id;
      const marker = new maps.Marker({ position: pos, map, icon: vehicleIcon(maps, v, selected), zIndex: selected ? 999 : 1 });
      marker.addListener("click", () => {
        focusOn(pos);
        onSelect?.(v);
      });
      overlaysRef.current.push(marker);
      if (selected) {
        overlaysRef.current.push(
          new maps.Circle({ center: pos, radius: 12, map, fillOpacity: 0, strokeColor: STATUS_COLOR[v.status] || STATUS_COLOR.parked, strokeOpacity: 0.6, strokeWeight: 1.5 })
        );
      }
      extend(pos);
    });

    // Fit the viewport to whatever's on the map once, on first real data —
    // not on every redraw, or a live-polling map would yank the user's
    // pan/zoom back to "fit everything" every refresh cycle.
    if (hasBoundsPoint && !boundsFitRef.current) {
      map.fitBounds(bounds, 60);
      boundsFitRef.current = true;
    }
  }, [maps, vehicles, geofences, facilities, route, selectedId, showBoundaries, onSelect, focusOn]);

  if (error) {
    return (
      <div data-testid={testId} className={cn("relative flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border border-border/70 bg-[#0a0a0b] px-6 text-center text-sm text-muted-foreground", className)}>
        <MapPinOff className="h-6 w-6" />
        Map unavailable — Google Maps API key is missing or invalid.
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-testid={testId}
      className={cn(
        "relative h-full w-full overflow-hidden rounded-2xl border border-border/70 bg-[#0a0a0b]",
        className,
        // Rounded corners and a border make sense inside a page card, but read
        // as a broken frame when this element *is* the whole screen.
        isFullscreen && "!rounded-none !border-0"
      )}
    >
      <div ref={containerRef} className="h-full w-full" />

      {/* Gated on `controls` too: the decorative auth-screen map and the small
          embedded mini-maps have no controls and no room for a search box. */}
      {controls && search && maps && mapInstance && <MapSearch maps={maps} map={mapInstance} />}

      {overlay}

      {controls && (
        <>
          <div className="absolute right-3 top-3 flex flex-col gap-1.5">
            <button
              data-testid="map-zoom-in"
              onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() || DEFAULT_ZOOM) + 1)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 bg-card/80 text-foreground/80 backdrop-blur transition hover:bg-card hover:text-foreground"
            >
              <Plus className="h-4 w-4" />
            </button>
            <button
              data-testid="map-zoom-out"
              onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() || DEFAULT_ZOOM) - 1)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 bg-card/80 text-foreground/80 backdrop-blur transition hover:bg-card hover:text-foreground"
            >
              <Minus className="h-4 w-4" />
            </button>
          </div>
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
            <button
              data-testid="map-locate"
              onClick={() => {
                if (!navigator.geolocation || !mapRef.current) return;
                navigator.geolocation.getCurrentPosition((pos) => {
                  mapRef.current.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                  mapRef.current.setZoom(14);
                });
              }}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 bg-card/80 text-foreground/80 backdrop-blur transition hover:bg-card hover:text-foreground"
            >
              <Locate className="h-4 w-4" />
            </button>
            <button
              data-testid="map-fullscreen"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 bg-card/80 text-foreground/80 backdrop-blur transition hover:bg-card hover:text-foreground"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              data-testid="map-layer-toggle"
              onClick={() => setLayer((l) => (l === "satellite" ? "dark" : "satellite"))}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-border/70 bg-card/80 px-3 text-xs font-medium text-foreground/80 backdrop-blur transition hover:bg-card hover:text-foreground"
            >
              <Layers className="h-3.5 w-3.5" /> {isSat ? "Map" : "Satellite"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function MapLegend({ className }) {
  const items = [
    { c: STATUS_COLOR.moving, l: "Moving" },
    { c: STATUS_COLOR.parked, l: "Parked" },
    { c: STATUS_COLOR.idle, l: "Idle" },
    { c: STATUS_COLOR.offline, l: "Offline" },
    { c: STATUS_COLOR.alert, l: "Alert" },
  ];
  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl border border-border/60 bg-card/60 px-3 py-2 text-xs", className)}>
      {items.map((i) => (
        <span key={i.l} className="flex items-center gap-1.5 text-muted-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: i.c }} /> {i.l}
        </span>
      ))}
    </div>
  );
}

// Legend for the speed-band-colored trip route (see SPEED_BANDS above).
export function SpeedBandLegend({ className }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl border border-border/60 bg-card/60 px-3 py-2 text-xs", className)}>
      {SPEED_BANDS.map((b) => (
        <span key={b.label} className="flex items-center gap-1.5 text-muted-foreground">
          <span className="h-2 w-2 rounded-full" style={{ background: b.color }} /> {b.label}
        </span>
      ))}
    </div>
  );
}
